import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Checkbox } from 'components/ui/checkbox'
import { Badge } from 'components/ui/badge'
import {
	initializeTemplates,
	initializeTemplateScoringCriteria,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateScoringCriterion,
	deleteTemplateScoringCriterion,
} from 'slices/templateSlice'
import { GripVertical, Plus, Trash2, LayoutTemplate, Loader2 } from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Types ──────────────────────────────────────────────────────────────────────

interface CriterionData {
	_id: string
	name: string
	category: string
	weight: number
	description?: string
	isNew?: boolean
}

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	criterion: CriterionData
	onNameChange: (id: string, value: string) => void
	onCategoryChange: (id: string, value: string) => void
	onWeightChange: (id: string, value: number) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	criterion,
	onNameChange,
	onCategoryChange,
	onWeightChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_1fr_180px_90px_32px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			<div
				{...attributes}
				{...listeners}
				className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			<Input
				value={criterion.name}
				onChange={e => onNameChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Criterion name"
			/>

			<Input
				value={criterion.category}
				onChange={e => onCategoryChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Category (e.g. Demographics)"
			/>

			<Input
				type="number"
				min={0}
				value={Number.isFinite(criterion.weight) ? criterion.weight : 0}
				onChange={e => onWeightChange(id, parseFloat(e.target.value) || 0)}
				className="h-7 text-xs text-right"
				placeholder="0"
			/>

			<div className="flex items-center justify-center">
				<Checkbox
					checked={isSelected}
					onCheckedChange={checked => onSelect(id, !!checked)}
				/>
			</div>

			<button
				type="button"
				title="Delete criterion"
				onClick={() => onDelete(id)}
				className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const ScoringCriteriaTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector(
		(state: any) => state.templates,
	)
	const {
		entities: criterionEntities,
		ids: criterionIds,
		isLoading: criteriaLoading,
	} = useSelector((state: any) => state.templates.scoringCriteria)

	const scoringTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'scoring')
			.sort((a: any, b: any) =>
				(a.templateName || '').localeCompare(b.templateName || ''),
			)
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [criterionOrder, setCriterionOrder] = useState<string[]>([])
	const [criteriaMap, setCriteriaMap] = useState<Record<string, CriterionData>>({})
	const [selectedCriteria, setSelectedCriteria] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalCriterionIds, setOriginalCriterionIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateScoringCriteria(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		const loaded = (criterionIds || [])
			.map((id: string) => criterionEntities[id])
			.filter(Boolean)
			.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

		const order: string[] = []
		const map: Record<string, CriterionData> = {}
		const origIds = new Set<string>()
		loaded.forEach((c: any) => {
			order.push(c._id)
			map[c._id] = {
				_id: c._id,
				name: c.name || '',
				category: c.category || '',
				weight: typeof c.weight === 'number' ? c.weight : 0,
				description: c.description || '',
			}
			origIds.add(c._id)
		})
		setCriterionOrder(order)
		setCriteriaMap(map)
		setOriginalCriterionIds(origIds)
		setSelectedCriteria(new Set())
		setErrors({})
		setIsDirty(false)
	}, [criterionIds, criterionEntities, selectedTemplateId, isCreateMode, templateEntities])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setCriterionOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Row Handlers ───────────────────────────────────────────────────────────

	const handleNameChange = (id: string, value: string) => {
		setCriteriaMap(prev => ({ ...prev, [id]: { ...prev[id], name: value } }))
		setIsDirty(true)
	}

	const handleCategoryChange = (id: string, value: string) => {
		setCriteriaMap(prev => ({ ...prev, [id]: { ...prev[id], category: value } }))
		setIsDirty(true)
	}

	const handleWeightChange = (id: string, value: number) => {
		setCriteriaMap(prev => ({ ...prev, [id]: { ...prev[id], weight: value } }))
		setIsDirty(true)
	}

	const handleSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedCriteria)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedCriteria(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedCriteria(checked ? new Set(criterionOrder) : new Set())
	}

	const isAllSelected =
		criterionOrder.length > 0 && criterionOrder.every(id => selectedCriteria.has(id))

	const handleDeleteCriterion = (id: string) => {
		const criterion = criteriaMap[id]
		const name = criterion?.name?.trim() || 'this criterion'
		notify({
			title: 'Delete Criterion',
			text: `Are you sure you want to delete "${name}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setCriterionOrder(prev => prev.filter(cid => cid !== id))
				setCriteriaMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedCriteria(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedCriteria.size === 0) return
		notify({
			title: 'Delete Selected Criteria',
			text: `Are you sure you want to delete ${selectedCriteria.size} criterion${
				selectedCriteria.size > 1 ? 'ia' : ''
			}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setCriterionOrder(prev => prev.filter(id => !selectedCriteria.has(id)))
				setCriteriaMap(prev => {
					const next = { ...prev }
					selectedCriteria.forEach(id => delete next[id])
					return next
				})
				setSelectedCriteria(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddCriterion = () => {
		const tempId = `_new_${nextTempId.current++}`
		const newCriterion: CriterionData = {
			_id: tempId,
			name: '',
			category: '',
			weight: 0,
			isNew: true,
		}
		setCriterionOrder(prev => [...prev, tempId])
		setCriteriaMap(prev => ({ ...prev, [tempId]: newCriterion }))
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setCriterionOrder([])
			setCriteriaMap({})
			setOriginalCriterionIds(new Set())
			setSelectedCriteria(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate],
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate],
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyCriterion = criterionOrder.find(id => !criteriaMap[id]?.name.trim())
		if (emptyCriterion) newErrors.criteria = 'All criteria must have a name'
		const zeroWeight = criterionOrder.find(id => !((criteriaMap[id]?.weight ?? 0) > 0))
		if (zeroWeight) newErrors.criteria = 'All criteria must have a weight greater than 0'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			if (isCreateMode) {
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'scoring',
						accessLevel,
					}),
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				for (let i = 0; i < criterionOrder.length; i++) {
					const c = criteriaMap[criterionOrder[i]]
					await (dispatch as any)(
						createTemplateScoringCriterion({
							templateId: newTemplateId,
							name: c.name.trim(),
							category: c.category.trim() || null,
							weight: c.weight,
							description: c.description || null,
							seq: i + 1,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
					}),
				)

				// Replace-all: delete originals, recreate in current order. Mirrors the
				// pattern used by TasksTemplateCN — the alternative (per-row PUT/POST)
				// is more code and gives identical results.
				for (const origId of originalCriterionIds) {
					await (dispatch as any)(
						deleteTemplateScoringCriterion({
							templateId: selectedTemplateId,
							_id: origId,
						}),
					)
				}
				for (let i = 0; i < criterionOrder.length; i++) {
					const c = criteriaMap[criterionOrder[i]]
					await (dispatch as any)(
						createTemplateScoringCriterion({
							templateId: selectedTemplateId,
							name: c.name.trim(),
							category: c.category.trim() || null,
							weight: c.weight,
							description: c.description || null,
							seq: i + 1,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateScoringCriteria(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Derived ────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled =
		!templateName.trim() || !description.trim() || criterionOrder.length === 0 || isSaving

	const totalWeight = useMemo(
		() => criterionOrder.reduce((sum, id) => sum + (criteriaMap[id]?.weight || 0), 0),
		[criterionOrder, criteriaMap],
	)

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">
							Scoring Criteria Templates
						</h2>

						<div className="max-w-[1080px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{scoringTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : scoringTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">
											No templates yet
										</div>
									) : (
										scoringTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">
															{template.description}
														</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button
										variant="ghost"
										onClick={handleNewTemplate}
										className="w-full justify-start"
									>
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<LayoutTemplate className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName)
															setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${
														errors.templateName ? 'border-red-500' : ''
													}`}
													placeholder="Template Name"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.templateName}
													</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description)
															setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${
														errors.description ? 'border-red-500' : ''
													}`}
													placeholder="Describe what kind of site this rubric is for"
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.description}
													</p>
												)}
											</div>
										</div>

										{/* Access Level + Total Weight */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs items-center">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private'
														? 'Private'
														: level === 'org-wide'
															? 'Org-Wide'
															: 'Public'}
												</label>
											))}
											<div className="flex-1" />
											<span className="text-gray-600">
												{criterionOrder.length} criteria · total weight
											</span>
											<Badge
												className={`text-[10px] ${
													totalWeight === 100
														? 'bg-emerald-100 text-emerald-700'
														: totalWeight === 0
															? 'bg-gray-200 text-gray-700'
															: 'bg-amber-100 text-amber-700'
												}`}
											>
												{totalWeight}
											</Badge>
										</div>

										{/* Criteria Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											<div className="grid grid-cols-[24px_32px_1fr_180px_90px_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span className="text-center">Seq</span>
												<span>Criterion Name</span>
												<span>Category</span>
												<span className="text-right pr-1">Weight</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox
														checked={isAllSelected}
														onCheckedChange={checked => handleSelectAll(!!checked)}
													/>
												</label>
												<span></span>
											</div>

											{criteriaLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : criterionOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No criteria yet. Click "+ Add Criterion" to get started.
												</div>
											) : (
												<DndContext
													sensors={sensors}
													collisionDetection={closestCenter}
													onDragEnd={handleDragEnd}
												>
													<SortableContext
														items={criterionOrder}
														strategy={verticalListSortingStrategy}
													>
														{criterionOrder.map((id, index) => {
															const criterion = criteriaMap[id]
															if (!criterion) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	criterion={criterion}
																	onNameChange={handleNameChange}
																	onCategoryChange={handleCategoryChange}
																	onWeightChange={handleWeightChange}
																	onDelete={handleDeleteCriterion}
																	isSelected={selectedCriteria.has(id)}
																	onSelect={handleSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.criteria && (
												<p className="text-red-500 text-[0.7rem] px-3 py-1">
													{errors.criteria}
												</p>
											)}
										</div>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddCriterion}>
													<Plus className="w-4 h-4 mr-1" />
													Add Criterion
												</Button>
												{selectedCriteria.size > 0 && (
													<Button
														variant="ghost-red"
														onClick={handleDeleteSelected}
													>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedCriteria.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default ScoringCriteriaTemplateCN
